import moment from 'moment-timezone';
import { BookingDateSummaryInstance } from './formatBookings';
import { ProfileBookingGroup } from './groupBookingSeries';

type EndedCheckReservation = {
  endTime?: string | null;
  timeZone?: string | null;
};

/**
 * Whether a reservation is already over. Open-ended reservations never are.
 * The zone only affects parsing; the comparison itself is between instants.
 */
export const hasReservationEnded = (
  reservation: EndedCheckReservation,
  fallbackZone: string | null | undefined,
  now: moment.Moment = moment()
): boolean => {
  if (!reservation.endTime) {
    return false;
  }
  const zone = reservation.timeZone ?? fallbackZone ?? 'UTC';
  return !moment.tz(reservation.endTime, zone).isAfter(now);
};

/** One series' instances, as returned by `getDeskReservationInstancesBySeriesIds`. */
export type SeriesInstance = {
  startTime: string;
  endTime?: string | null;
  timeZone?: string | null;
};

/**
 * Attaches each series booking's full remaining run of instances, so its
 * date line describes the whole series and not just the instances that fell
 * inside the panel's loaded window.
 *
 * Instances that have already ended are dropped, matching the windowed list,
 * so the label reads as the days still to come. A series with no remaining
 * instances (or one the lookup has not returned yet) keeps the windowed
 * instances as its summary, so the card never goes blank while loading.
 */
export const withSeriesSummaryInstances = (
  groups: ProfileBookingGroup[],
  instancesBySeriesId: Record<string, SeriesInstance[] | undefined>,
  now: moment.Moment = moment()
): ProfileBookingGroup[] =>
  groups.map((group) => {
    const { seriesId } = group.reservation;
    const seriesInstances = seriesId
      ? instancesBySeriesId[seriesId]
      : undefined;
    if (!seriesInstances) {
      return group;
    }

    const fallbackZone =
      group.reservation.timeZone ?? group.reservation.seat.location.timezone;
    const summaryInstances: BookingDateSummaryInstance[] = seriesInstances
      .filter((instance) => !hasReservationEnded(instance, fallbackZone, now))
      .map((instance) => ({
        startTime: instance.startTime,
        endTime: instance.endTime,
        timeZone: instance.timeZone ?? fallbackZone,
      }));

    return summaryInstances.length > 0 ? { ...group, summaryInstances } : group;
  });
